import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import Input from "../../forms/Input";
import PasswordInput from "../../forms/PasswordInput";
import ChangePasswordForm from "./ChangePasswordForm";
import Checkbox from "../../forms/Checkbox";
import Button from "../../buttons/Button";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import withRouter from "../../utils/withRouter";
import { getSrvCfg } from '../../utils/storage';
import withStorage from "../../utils/withStorage";
import withRequests from "../../utils/withRequests";
import LoadingPanel from "../../LoadingPanel";
import GoogleButton from "../../buttons/GoogleButton";
import CoraError from "../../utils/coraError";
import { getClientVersion, afterLoginHandle, getURLParamsByNames } from "../../utils/helper";
import "./FullscreenLogin.scss";
import { useDidMount } from "../../utils/hooks";
import { SafeSVG } from "../../svg/SafeSVG";

const USER_KEY = 'User';
const USERNAME_KEY = "username";

const googleRedirectURI = () => {
  return `${window.location.origin}?provider=1`;
};

/**
 * Login component
 * @module
 * @param {object} [user] - User
 * @param {bool} isLoading - Is loading?
 * @param {bool} [rememberDisabled] - Remember disabled
 * @param {func} signIn - Sign in function
 * @param {object} [afterLoginlocation] - After login location
 * @param {string} [userNameLabel] - User name label, "Prihlasovacie meno" if not provided
 * @param {string} [passwordLabel] - Password label, "Heslo" if not provided
 * @param {string} [logoUrl] - Logo url
 * @param {string} [backgroundUrl] - Background image url
 * @param {string} [appTitle] - Application title
 * @param {string} [appFullName] - Application full name
 * @param {bool} [showForgotPassword] - Show button for forgot Password
 * @param {func} [showMsg] - Function to show message to user
 */
const Login = (props) => {
  const username = props.rememberDisabled
    ? ""
    : localStorage.getItem(USERNAME_KEY);

  const { guid, code, provider, expired, rt, unlock } = getURLParamsByNames([
    "guid",
    "code",
    "provider",
    "expired",
    "rt",
    "unlock"
  ]);

  const [state, setState] = useState({
    isLoading: false,
    username: username || "",
    password: null,
    matchPassword: "",
    remember: username !== null,
    guid,
    code,
    provider,
    changePassword: false,
    changePasswordText: null,
    expired,
    unlock,
    isOpenAlert: false,
    l_2FA: false,
    rt
  });

  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [mfaIndex, setMfaIndex] = useState(0);
  const [invalidTry, setInvalidTry] = useState(false);
  const navigate = useNavigate();

  const reset = (username) => {
    setState({
      isLoading: false,
      username: username || "",
      password: "",
      matchPassword: "",
      remember: false,
      guid: null,
      code: null,
      provider: null,
      changePassword: false,
      changePasswordPattern: null,
      changePasswordText: null,
      expired: null,
      isOpenAlert: false,
      rt: null
    });
  };

  useDidMount(async () => {
    const { guid, code, provider, rt, unlock } = state;
    const { verifyGUID, oAuthLogin } = props;

    if (unlock && code) {
      const unlockSuccess = await props.requests.unlockAccount(code);
      if (unlockSuccess) {
        props.showMsg("Váš účet bol odblokovaný,\nmôžete sa prihlásiť alebo požiadať o zmenu hesla");
      } else {
        props.showMsg("Váš účet sa nepodarilo odblokovať,\npočkajte chvíľu a pokúste sa prihlásiť", "error");
      }
      navigate("/");
    }
    if (code && provider) {

      // odstránenie argumentov za ? aktuálnej URL bez opätovného načítania stránky
      // je niečo za ? (otáznikom) ?
      if (window.location.search?.length > 1) {
        // sú k dispozícii nové metódy histórie ?
        if (window.history !== undefined && window.history.pushState !== undefined) {
          // ak existuje pushstate, pridať nový stav do histórie, toto zmení URL bez opätovného načítania stránky
          window.history.pushState({}, document.title, window.location.pathname);
        }
      }
      await oAuthLogin(props, code, provider);

      if (props.user !== null) return;
      navigate("/");
    }
    else if (guid) {
      await verifyGUID(guid);
      navigate("/");
    }
    else if (code) {
      getChangePasswordText();
      setState(prevState => ({ ...prevState, changePassword: true }));
    }
    else if (rt) {
      await redirect(rt);
      navigate("/");
    }
  });

  const redirect = async (token) => {
    await props.requests.signInRedirect(token);
    const user = sessionStorage.getItem(USER_KEY);
    if (user) window.location.reload();
  };

  const getChangePasswordText = async () => {
    setState(prevState => ({ ...prevState, isLoading: true }));
    try {
      const pattern = await props.requests.getPattern(state.code);
      setState(prevState => ({ ...prevState, changePasswordText: pattern?.PatternText, changePasswordPattern: pattern?.Pattern }));
    }
    catch (error) {
      if (error instanceof CoraError && error.code === "401.000") {
        props.showMsg("Odkazu na zmenu hesla vypršala platnosť.", "error");
        navigate("/");
      }
      else {
        props.showMsg("Neočakávaná chyba.", "error");
      }
    }
    setState(prevState => ({ ...prevState, isLoading: false }));
  };

  const handleChange = (name, value) => {
    setState(prevState => ({ ...prevState, [name]: value }));
  };


  const handleCloseDialog = () => {
    setState(prevState => ({ ...prevState, isOpenAlert: false }));
  };

  const handleCreateCode = async () => {
    setState(prevState => ({ ...prevState, isLoading: true }));
    const username = state.username.trim();
    try {
      await props.requests.createCode(username);
      setState(prevState => ({ ...prevState, isOpenAlert: true }));
    }
    catch (error) {
      if (error instanceof CoraError) {
        props.showMsg(error.message, "error");
      }
      else {
        props.showMsg("Neočakávaná chyba.", "error");
      }
    }
    setState(prevState => ({ ...prevState, isLoading: false }));
  };

  const handleMFaLogin = async () => {
    setState(prevState => ({ ...prevState, isLoading: true }));
    try {
      let res = await props.requests.signInMFa(state.username.trim(), state.password, mfaCode.join(''));
      if (res?.Code === "403.001") {
        setInvalidTry(true);
        new Promise(timer => setTimeout(timer, 3000)).then(() => {
          resetMfa();
        });
      }
      else {
        handleAfterSignIn(res);
      }
    } catch (error) {
      if (error instanceof CoraError) {
        props.showMsg(error.message, "error");
      }
      else {
        props.showMsg("Neočakávaná chyba.", "error");
      }
    }
    setState(prevState => ({ ...prevState, isLoading: false }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setState(prevState => ({ ...prevState, isLoading: true }));
    const username = state.username.trim();
    const password = state.password;
    try {
      let res = await props.requests.signIn(username, password);
      if (res?.Code === "403.001") {
        setState(prevState => ({ ...prevState, l_2FA: true }));
      }
      else {
        handleAfterSignIn(res);
      }
    } catch (error) {
      if (error instanceof CoraError) {
        props.showMsg(error.message, "error");
      }
      else {
        props.showMsg("Neočakávaná chyba.", "error");
      }
    }
    setState(prevState => ({ ...prevState, isLoading: false }));
  };

  const handleAfterSignIn = (res) => {
    if (props.afterSignIn) {
      props.afterSignIn(res);
    }
    if (!props.rememberDisabled) {
      if (state.remember) {
        localStorage.setItem(USERNAME_KEY, state.username.trim());
      }
      else {
        localStorage.removeItem(USERNAME_KEY);
      }
    }
  };

  useEffect(() => {
    if (props.user !== null) {
      afterLoginHandle(props);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user]);

  const keyHandler = (event) => {
    if (((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105))) {
      setMfaCode((prevState) => {
        let newState = [...prevState];
        newState[mfaIndex] = event.key;
        return newState;
      });
      setMfaIndex((prevState) => {
        return prevState === 5 ? prevState : prevState + 1;
      });
    }
    else if (event.keyCode === 8) {
      resetMfa();
    }
  };

  useEffect(() => {
    if (state.l_2FA) {
      if (mfaCode.find(x => x === "") !== undefined) {
        window.addEventListener("keyup", keyHandler);
        return () => {
          window.removeEventListener("keyup", keyHandler);
        };
      } else {
        handleMFaLogin();
      }
    }

  }, [mfaIndex, mfaCode, state.l_2FA]);  // eslint-disable-line

  const resetMfa = () => {
    setMfaCode(["", "", "", "", "", ""]);
    setMfaIndex(0);
    setInvalidTry(false);
  };

  const handleMfaEmail = async () => {
    setState(prevState => ({ ...prevState, isLoading: true }));
    const username = state.username.trim();
    const password = state.password;

    let res = await props.requests.generateMfaCode(username, password);
    props.showMsg(res.UserMessage, "ok");
    setState(prevState => ({ ...prevState, isLoading: false }));
  };

  const renderLogin = () => {

    const { children } = props;
    if (state.changePassword) {
      return (
        <ChangePasswordForm
          pattern={state.changePasswordPattern}
          patternText={state.changePasswordText}
          children={props.children}
          isLoading={props.isLoading}
          showMsg={props.showMsg}
          code={state.code}
          reset={reset}
          expired={state.expired}
        />
      );
    }
    else if (state.rt) {
      return (
        <form className="login-form">
          <div className="login-title">Prihlásenie</div>
          <div className="login-field">
            <span>Preberá sa prihlásenie z ekosystému CORA</span>
          </div>
        </form>
      );
    }
    else if (state.l_2FA) {
      return (
        <form className="login-form">
          <div className="login-title">Dvoj-faktorová autentifikácia</div>
          <div className="login-label">Zadajte 6 miestny kód</div>
          <div className="login-field">
            <div className="login-code-container">
              <div id="codeInput" className="login-code-inputs">
                {mfaCode.map((item, i) => {
                  return <input id={`login-code-input-${i}`} className={`login-code-input${(i === mfaIndex && !item ? " current" : "")} ${invalidTry ? "invalid" : ""}`} type="text" inputMode="numeric" maxLength="1" value={item} />;
                })}
              </div>
            </div>
            {invalidTry && <div className='invalid-label'>Zadaný kód je neplatný</div>}
            <span className="login-code-alternative">Ak nemôžete využit kód z overovacej aplikácie, môžete využiť možnosť zaslania kódu pomocou</span>
            <span className="login-code-alternative link" onClick={handleMfaEmail}>e-mailu.</span>
          </div>
        </form>
      );
    }
    else {
      const loginConfig = getSrvCfg().loginConfig;
      return (
        <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
          <div className="login-title">Prihlásenie</div>
          <div className="login-label">{props.userNameLabel ?? "Prihlasovacie meno"}</div>
          <div className="login-field">
            <Input
              name="username"
              required={true}
              onChange={handleChange}
              value={state.username}
              autoComplete="username"
            />
          </div>
          <PasswordInput
            label={props.passwordLabel ?? "Heslo"}
            name="password"
            required={true}
            onChange={handleChange}
            value={state.password}
            isInvalid={state.password === ""}
            autoComplete={"off"}
          />
          {!props.rememberDisabled && (
            <div className="login-checkbox">
              <Checkbox
                name="remember"
                onChange={handleChange}
                value={state.remember}
                label={"Zapamätaj si ma"}
              />
            </div>

          )}
          <Button
            primary={true}
            disabled={props.isLoading}
            label="Prihlásiť"
            type="submit"
          />
          {!loginConfig.google.disabled &&
            <GoogleButton
              redirectURI={googleRedirectURI()}
              state="logging"
              primary={false}
            />
          }
          {props.showForgotPassword && (
            <Button
              disabled={props.isLoading || state.username === null || state.username === ""}
              label="Zabudnuté heslo"
              onClick={handleCreateCode}
            />
          )}
          {children}
        </form>
      );
    }
  };
  const { loginFooter } = props;

  return (
    <>
      {props.backgroundUrl && (<div className="login-background"><img src={props.backgroundUrl} alt="" /></div>)}
      <div className="login-container">
        <div className="login-card">
          <div className="login-content">
            <div className="login-graphics">
              <div className="login-version-hidden">Verzia {getClientVersion()}</div>
              <SafeSVG className="login-logo" src={props.logoUrl} alt={`Logo aplikácie ${props.appFullName}`} />
              <div className="login-title">{props.appTitle}</div>
              <div className="login-name">{props.appFullName}</div>
              <div className="login-version">Verzia {getClientVersion()}</div>
            </div>
            {renderLogin()}
          </div>
          {loginFooter && <div className="login-footer">
            {loginFooter}
          </div>
          }
        </div>
        <LoadingPanel isLoading={props.isLoading || state.isLoading} />
        <ConfirmDialog
          isOpen={state.isOpenAlert}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          text={"Na Váš email bol odoslaný jednorazový odkaz pre zmenu hesla."}
        />
      </div>
    </>
  );
};

Login.propTypes = {
  user: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  logoUrl: PropTypes.string,
  backgroundUrl: PropTypes.string,
  appTitle: PropTypes.string,
  appFullName: PropTypes.string,
  rememberDisabled: PropTypes.bool,
  afterSignIn: PropTypes.func.isRequired,
  verifyGUID: PropTypes.func,
  oAuthLogin: PropTypes.func,
  userNameLabel: PropTypes.string,
  passwordLabel: PropTypes.string,
  afterLoginlocation: PropTypes.object,
  showForgotPassword: PropTypes.bool,
  loginFooter: PropTypes.element,
  showMsg: PropTypes.func.isRequired
};

export default withRouter(withStorage(withRequests(Login)));